'use client';

import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { FILE_VALIDATION } from '@/constants/variables';
import { useS3UploadMutation, useUploadMutation } from '@/hooks/mutations/useUploadMutation';
import { useQueryConfig } from '@/hooks/queries/useQueryConfig';
import {
  fileAcceptTypes,
  useFileUpload,
} from '@/hooks/utils';
import {
  createCourseService,
  createCourseStructureService,
  deleteCourseStructureByIdService,
} from '@/services/httpServices/AutocourseServices';
import type {
  CreateCourseResponse,
  CreateCourseServiceConfig,
  CreateCourseStructureResponse,
  CreateCourseStructureServiceConfig,
  DeleteCourseStructureServiceConfig,
} from '@/services/httpServices/AutocourseServices/types';
import type { PresignRequestServiceConfig } from '@/services/httpServices/UploadServices/types';
import type {
  AutocourseUploadFunction,
  FileOptions,
  StorageUploadMutationResult,
  UpdateProgressFunction,
} from '@/types/upload';

export function useAutocourseFileUpload(options?: {
  hasVideo2Course?: boolean;
}): StorageUploadMutationResult {
  const hasVideo2Course = options?.hasVideo2Course === true;
  const { httpRequestConfig } = useQueryConfig();
  const presignedUrlMutation = useUploadMutation();
  const s3UploadMutation = useS3UploadMutation();

  // Create a ref for the updateProgress function
  const updateProgressRef = useRef<UpdateProgressFunction>((_fileName: string, _progress: number) => {});

  // Create the file upload function to be passed to the hook
  const uploadFile: AutocourseUploadFunction = async (file: File): Promise<string> => {
    // Get the presigned URL from the server
    const presignResponse = await presignedUrlMutation.mutateAsync({
      httpRequestConfig,
      params: {
        body: {
          file: file.name,
          contentType: file.type,
        },
      },
    } as PresignRequestServiceConfig);

    // The actual upload to S3 is done with the returned presigned URL
    const controller = new AbortController();

    await s3UploadMutation.mutateAsync({
      presignedUrl: presignResponse.body.presignedUrl,
      file,
      onProgress: (progress: number) => {
        // Use the ref to access the latest updateProgress function
        updateProgressRef.current(file.name, progress);
      },
      signal: controller.signal,
    });

    return presignResponse.body.s3Path;
  };

  const effectiveAcceptType = hasVideo2Course
    ? fileAcceptTypes.autocourse
    : fileAcceptTypes.autocourseDocumentsOnly;

  // Initialize the hook with our upload function
  const {
    uploadProgress,
    updateProgress,
    cancelUpload,
    openFilePicker,
    isUploading,
    isFileUploading,
    getFileInfo,
    uploadHandler,
  } = useFileUpload({
    accept: effectiveAcceptType,
    uploadFunction: uploadFile,
  });

  // Update the ref when updateProgress changes
  useEffect(() => {
    updateProgressRef.current = updateProgress;
  }, [updateProgress]);

  const effectiveValidation = hasVideo2Course
    ? FILE_VALIDATION.documents
    : FILE_VALIDATION.documentsOnly;

  // Create customized file picker with doc upload defaults
  const openDocFilePicker = (options: FileOptions = {}) => {
    return openFilePicker({
      accept: effectiveAcceptType,
      ...options,
    });
  };

  // Create customized upload handler with type-specific size limits for documents
  const documentUploadHandler = (options: FileOptions = {}) => {
    return uploadHandler({
      accept: effectiveAcceptType,
      allowedTypes: effectiveValidation.allowedTypes,
      useTypeSpecificSizeLimit: true, // Enable type-specific size limits (PDF: 250MB, Word/Excel/PPT: 200MB)
      ...options,
    });
  };

  const isUploadingComposite = isUploading || presignedUrlMutation.isPending || s3UploadMutation.isPending;

  // Convert error to string if it's an Error object
  const errorMessage = (() => {
    const presignedError = presignedUrlMutation.error;
    const s3Error = s3UploadMutation.error;

    if (presignedError !== null && presignedError !== undefined) {
      return presignedError instanceof Error ? presignedError.message : String(presignedError);
    }
    if (s3Error !== null && s3Error !== undefined) {
      return s3Error instanceof Error ? s3Error.message : String(s3Error);
    }
    return undefined;
  })();

  return {
    uploadFile,
    cancelUpload,
    openFilePicker: openDocFilePicker,
    uploadProgress,
    isUploading: isUploadingComposite,
    isFileUploading,
    isFileLoading: (fileName: string, hasCompleted: boolean) => {
      return hasCompleted === false && isUploadingComposite && (fileName in uploadProgress || uploadProgress[fileName] === 0);
    },
    getFileInfo,
    error: errorMessage,
    uploadHandler: documentUploadHandler,
  };
}

export function useCreateCourseStructureMutation() {
  return useMutation<
    CreateCourseStructureResponse,
    Error,
    CreateCourseStructureServiceConfig
  >({
    mutationFn: createCourseStructureService,
  });
}

export function useCreateAutoCourse() {
  return useMutation<
    CreateCourseResponse,
    Error,
    CreateCourseServiceConfig
  >({
    mutationFn: createCourseService,
  });
}

export function useDeleteCourseStructureMutation() {
  return useMutation<undefined, Error, DeleteCourseStructureServiceConfig>({
    mutationFn: deleteCourseStructureByIdService,
  });
}
